import React from 'react';
import styled from 'styled-components';
import ReplayIcon from '@mui/icons-material/Replay';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';

export interface ErrorMessageProps {
  errorMessage: string;
  allowRetry: boolean;
  showPlanUpgrade: boolean;
  onRetry: () => void;
  onShowPlans: () => void;
}

function ErrorMessage({
  errorMessage,
  allowRetry,
  showPlanUpgrade,
  onRetry,
  onShowPlans,
}: ErrorMessageProps) {
  return (
    <ErrorMessageStrip>
      <MessageStripContentArea>
        <MessageStripTextArea>
          <ErrorText>{errorMessage}</ErrorText>
          <ErrorButtonContainer>
            {allowRetry && (
              <ErrorRetryButton onClick={onRetry}>
                <ReplayIcon style={{ fontSize: '0.9rem', marginRight: '0.4rem' }} />
                Tentar novamente
              </ErrorRetryButton>
            )}
            {showPlanUpgrade && (
              <ErrorPlansButton onClick={onShowPlans}>
                <AccountCircleOutlinedIcon style={{ fontSize: '0.9rem', marginRight: '0.3rem' }} />
                Ver planos
              </ErrorPlansButton>
            )}
          </ErrorButtonContainer>
        </MessageStripTextArea>
      </MessageStripContentArea>
    </ErrorMessageStrip>
  );
}

export default ErrorMessage;

// ============ Styled Components ============

const ErrorMessageStrip = styled.div`
  display: flex;
  width: 100%;
  max-width: 100%;
  justify-content: flex-start;
  padding: 10px;
  position: relative;
`;

const MessageStripContentArea = styled.div`
  display: flex;
  justify-content: flex-start;
  color: ${({ theme }) => theme.font_color};
  width: 100%;
  max-width: 100%;
  margin: 0px;
`;

const MessageStripTextArea = styled.div`
  display: flex;
  flex-direction: column;
  font-align: left;
  font-size: 1rem;
  line-height: 1.8rem;
  padding: 10px;
  max-width: 100%;
  overflow-wrap: break-word;
  font-family: 'Sora', 'Lexend', sans-serif;
  @media (max-width: 600px) {
    max-width: 100%;
  }
  & p { white-space: pre-line; }
`;

const ErrorText = styled.div`
  color: ${props => props.theme.error_message_text};
  font-size: 1.1rem;
  line-height: 2.2rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  margin-bottom: 0;
`;

const ErrorButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  align-items: center;
`;

const ErrorRetryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.error_retry_button_bg};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  white-space: nowrap;
  min-width: auto;
  font-family: 'Noka', sans-serif;

  &:hover {
    background-color: ${props => props.theme.error_retry_button_hover};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorPlansButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.error_plans_button_bg};
  color: #ffffff;
  border: 1px solid transparent;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s, border-color 0.2s;
  white-space: nowrap;
  min-width: auto;
  font-family: 'Noka', sans-serif;

  &:hover {
    background-color: ${props => props.theme.error_plans_button_hover};
    border-color: ${props => props.theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.32)' : 'transparent'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
`;
